import React, { useEffect, useState } from 'react';
import { getInventory } from '../api/gameBuilderAPI';

/**
 * The item inventory (backend inventory/<id>.yaml): every item a player can use in a game - what it's for, its
 * states and the actions it offers - and "Try it", which plays the item's own sample mission (standard data
 * from its file) in the real game player. This is where an item's mechanics are judged before they appear in
 * a mission. Read-only: items are added as files (see inventory/README.md), or made from a mission's new objects
 * (marked To review).
 */
const MECHANIC = { confirm: '👆 button', choose: '☑️ pick one', amount: '⚖️ measure' };
const PAGE_SIZE = 10;
const MAX_TOGETHER = 10;
const PRESET = 5;              // "Try together" starts with the first five on the page ticked
// Item pictures are served by the backend (as the game's are) - see MissionGame.
const API_BASE = process.env.REACT_APP_API_URL || '';
const asset = (src) => (src && src.startsWith('/game-builder-assets') ? `${API_BASE}${src}` : src);

export default function InventoryCatalog({ onClose }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [picked, setPicked] = useState(null);   // ids to try together (null: the page's first five)
  // items made from a mission's new objects (backend game.promote_new_objects) are marked to be reviewed
  const [onlyReview, setOnlyReview] = useState(false);
  const toReview = (items || []).filter((it) => it.review);
  const listed = onlyReview ? toReview : (items || []);
  const pages = Math.max(1, Math.ceil(listed.length / PAGE_SIZE));
  const shown = listed.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const tryable = (it) => it.id !== 'patient' && it.problems.length === 0;
  const together = picked ?? shown.filter(tryable).slice(0, PRESET).map((it) => it.id);
  const toggle = (id) => setPicked((p) => {
    const now = p ?? together;
    return now.includes(id) ? now.filter((x) => x !== id) : now.length < MAX_TOGETHER ? [...now, id] : now;
  });
  // Trying out opens its own tab (/game-builder/try - GamePages.jsx), so the catalog stays as it was.
  const openTry = (ids) => window.open(`/game-builder/try?items=${ids.map(encodeURIComponent).join(',')}`, '_blank', 'noopener');
  const pager = items && listed.length > PAGE_SIZE && (
    <nav className="gb-pager" aria-label="Pages">
      <button type="button" className="gb-btn gb-btn-quiet gb-btn-sm" disabled={page === 0} onClick={() => { setPage((p) => p - 1); setPicked(null); }}>‹ Previous</button>
      <span className="gb-muted gb-small">Items {page * PAGE_SIZE + 1}–{Math.min(listed.length, (page + 1) * PAGE_SIZE)} of {listed.length}</span>
      <button type="button" className="gb-btn gb-btn-quiet gb-btn-sm" disabled={page >= pages - 1} onClick={() => { setPage((p) => p + 1); setPicked(null); }}>Next ›</button>
    </nav>
  );
  useEffect(() => { getInventory().then((d) => setItems(d.items)).catch((e) => setError(e.message)); }, []);

  return (
    <div className="gb-catalog">
      <header className="gb-catalog-head">
        <div>
          <h2>Item inventory</h2>
          <p className="gb-muted gb-small">Every item a player uses in a game comes from here. Try each one with its standard data.</p>
        </div>
        <button type="button" className="gb-btn gb-btn-quiet" onClick={onClose}>Back to plans</button>
      </header>
      {error && <div className="gb-error" role="alert"><span>{error}</span></div>}
      {!items && !error && <p className="gb-muted"><span className="gb-spinner" /> Loading the inventory…</p>}
      {items && (
        <div className="gb-together">
          <button type="button" className="gb-btn" disabled={together.length < 2} onClick={() => openTry(together)}>
            ▶ Try together ({together.length})
          </button>
          <button type="button" className="gb-btn gb-btn-quiet" disabled={!together.length} onClick={() => setPicked([])}>Deselect all</button>
          <span className="gb-small gb-muted">Tick up to {MAX_TOGETHER} items below to try them side by side in one scene.</span>
          {toReview.length > 0 && (
            <button type="button" className={`gb-btn gb-btn-sm ${onlyReview ? '' : 'gb-btn-quiet'}`} aria-pressed={onlyReview}
              onClick={() => { setOnlyReview((v) => !v); setPage(0); setPicked(null); }}>
              🔎 To review ({toReview.length})
            </button>
          )}
        </div>
      )}
      {pager}
      <div className="gb-catalog-grid">
        {shown.map((it) => (
          <section key={it.id} className="gb-card gb-catalog-card">
            <div className="gb-catalog-picture">{it.picture ? <img src={asset(it.picture)} alt="" /> : <span>{it.icon}</span>}</div>
            <h3>{it.name} <span className="gb-muted gb-small">v{it.version}</span>
              {it.review && <span className="gb-tag gb-review" title="Made from a mission's new object - check it">To review</span>}</h3>
            {tryable(it) && (
              <label className="gb-small gb-inline">
                <input type="checkbox" checked={together.includes(it.id)} onChange={() => toggle(it.id)}
                  disabled={!together.includes(it.id) && together.length >= MAX_TOGETHER} /> Try together
              </label>
            )}
            <p className="gb-small">{it.for}</p>
            <ol className="gb-catalog-states">
              {it.states.map((st) => (
                <li key={st.name}>
                  <strong>{st.name}</strong>{st.start ? ' (start)' : ''}{st.effect && st.effect !== 'none' ? ` · ${st.effect}s` : ''}
                  {st.conveys && <span className="gb-muted"> - “{st.conveys}”</span>}
                  {st.actions.length > 0 && (
                    <ul>{st.actions.map((a) => (
                      <li key={a.id}>{MECHANIC[a.mechanic] || a.mechanic}: <em>{a.label}</em>{a.to ? ` → ${a.to}` : ''}{a.records ? ` · records ${a.records}` : ''}</li>
                    ))}</ul>
                  )}
                </li>
              ))}
            </ol>
            <p className="gb-small gb-muted">{(it.tags || []).join(' · ')}</p>
            {it.problems.length > 0
              ? <p className="gb-error gb-small">{it.problems[0]}</p>
              : <button type="button" className="gb-btn" onClick={() => openTry([it.id])}>▶ Try it</button>}
          </section>
        ))}
      </div>
      {pager}
    </div>
  );
}
